import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useCart } from '@/lib/cart';
import { useWishlist } from '@/lib/wishlist';
import { useSEO } from '@/lib/useSEO';
import { resolveImageUrl } from '@/components/ImageUploader';
import { formatCOP } from '@/lib/format';
import { BookmarkPlus, ShoppingCart, X, Flame } from 'lucide-react';
import { toast } from 'sonner';

const PRIORITY_META = {
  1: { label: 'Alta', cls: 'bg-rose-100 text-rose-800 border-rose-300' },
  2: { label: 'Media', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  3: { label: 'Baja', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
};

const PLACEHOLDER = 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=400';

export default function Wishlist() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const { add } = useCart();
  const { refresh } = useWishlist();

  useSEO({
    title: 'Lista de deseos | AutoMarket Colombia',
    description: 'Guarda repuestos que quieres comprar más adelante, agrega notas y prioridades.',
  });

  const load = () => {
    setLoading(true);
    api.get('/wishlist').then(({ data }) => setEntries(data.entries || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const remove = async (pid) => {
    await api.delete(`/wishlist/${pid}`);
    setEntries((es) => es.filter((e) => e.product_id !== pid));
    refresh();
  };

  const changePriority = async (entry, priority) => {
    await api.post('/wishlist', { product_id: entry.product_id, note: entry.note, priority });
    setEntries((es) => es.map((e) => e.product_id === entry.product_id ? { ...e, priority } : e));
  };

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Mi cuenta</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1 flex items-center gap-2">
          <BookmarkPlus className="h-7 w-7 text-amber-600" /> Lista de deseos
        </h1>
        <p className="mt-2 text-sm text-slate-600">Repuestos que tienes en la mira. Marca la prioridad y deja notas.</p>

        {!loading && entries.length === 0 && (
          <div data-testid="wishlist-empty" className="mt-8 border border-slate-200 bg-slate-50 p-12 text-center">
            <BookmarkPlus className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-4 text-slate-600">Aún no tienes productos en tu lista de deseos.</p>
            <Link to="/catalogo" className="mt-4 inline-block">
              <Button className="rounded-none bg-slate-900 hover:bg-slate-800">Explorar catálogo</Button>
            </Link>
          </div>
        )}

        {entries.length > 0 && (
          <div data-testid="wishlist-list" className="mt-6 border border-slate-200 bg-white divide-y divide-slate-200">
            {entries.map((e) => {
              const p = e.product;
              const img = resolveImageUrl(p.images?.[0]) || PLACEHOLDER;
              const meta = PRIORITY_META[e.priority] || PRIORITY_META[2];
              return (
                <div key={e.product_id} className="flex items-start gap-4 p-4" data-testid={`wishlist-row-${e.product_id}`}>
                  <Link to={`/producto/${p.slug || p.product_id}`} className="block h-24 w-24 shrink-0 bg-slate-50">
                    <img src={img} alt="" className="h-full w-full object-cover" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/producto/${p.slug || p.product_id}`} className="block">
                      <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{p.brand} · {p.category}</div>
                      <div className="font-display font-bold text-slate-900 text-base truncate hover:text-blue-700">{p.name}</div>
                    </Link>
                    <div className="mt-1 text-xs text-slate-500 font-mono">Ref: {p.reference}</div>
                    {e.note && <p className="mt-1 text-sm text-slate-700 italic">&ldquo;{e.note}&rdquo;</p>}
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.cls}`}>
                        <Flame className="h-3 w-3" /> {meta.label}
                      </span>
                      {[1, 2, 3].map((pr) => (
                        <button
                          key={pr}
                          onClick={() => changePriority(e, pr)}
                          data-testid={`wishlist-priority-${e.product_id}-${pr}`}
                          className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border transition ${
                            e.priority === pr
                              ? 'bg-slate-900 text-white border-slate-900'
                              : 'border-slate-300 text-slate-500 hover:border-slate-900 hover:text-slate-900'
                          }`}
                        >
                          {PRIORITY_META[pr].label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <div className="font-display font-black text-slate-900 text-lg">{formatCOP(p.price)}</div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        onClick={() => { add(p); toast.success(`${p.name} agregado al carrito`); }}
                        disabled={p.stock === 0}
                        data-testid={`wishlist-add-${e.product_id}`}
                        className="rounded-none bg-slate-900 hover:bg-slate-800 h-8 text-xs"
                      >
                        <ShoppingCart className="h-3 w-3 mr-1" /> Comprar
                      </Button>
                      <button
                        onClick={() => remove(e.product_id)}
                        data-testid={`wishlist-remove-${e.product_id}`}
                        className="h-8 w-8 inline-flex items-center justify-center border border-slate-200 hover:border-rose-500 hover:text-rose-600"
                        aria-label="Quitar"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
